import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { Plus, RotateCw, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { GD } from "src/GD";
import type { ITerminal } from "src/types/types";
import type { TPaymentChannel } from "../channelList/channelData";
import { AdminButton } from "../pages/administration/AdminControls";
import { channelStatus, compareText, formatDate, groupBy, matchesQuery, methodTypeLabel, plural, uniqueSorted } from "./providersData";
import {
	Cell,
	Chip,
	Chips,
	DirectoryCard,
	DirectoryRow,
	EmptyState,
	EntityCell,
	LinkButton,
	MethodTile,
	Mono,
	Muted,
	Notice,
	RowChevron,
	SearchField,
	SkeletonRows,
	TabIntro,
	TabSurface,
} from "./providersUi";

/** Payment methods (terminals) with the providers and channels that serve each of them. */
export const PaymentMethods = ({
	methods,
	channels,
	loading,
	busy,
	error,
	onReload,
	onShowChannels,
}: {
	methods: ITerminal[];
	channels: TPaymentChannel[];
	loading: boolean;
	busy: boolean;
	error?: IError;
	onReload: () => void;
	onShowChannels: (methodId: number) => void;
}) => {
	 useLocale();
	const [query, setQuery] = useState("");
	const usage = useMemo(() => groupBy(channels.filter(channel => channelStatus(channel) !== "deleted"), channel => channel.terminal?.id), [channels]);
	const visible = methods
		.filter(method => matchesQuery(query, [method.name, method.id, method.type, methodTypeLabel(method.type)]))
		.sort((a, b) => compareText(a.name, b.name));
	const add = () => GD.S_TERMINAL_EDITOR.invoke({ terminalID: 0 });
	const addButton = (
		<AdminButton variant="primarySmall" size="small" onClick={add}>
			<Plus size={14} />{msg("addMethod")}</AdminButton>
	);

	return (
		<TabSurface>
			<TabIntro
				text={msg("aPaymentMethodIsWhatTheCustomerPaysWithACardApplePayGooglePa_ffeea4d1")}
				actions={
					<>
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={onReload}>
							<RotateCw size={14} />{msg("refresh")}</AdminButton>
						{addButton}
					</>
				}
			/>
			{error && <Notice tone="error" action={<AdminButton variant="secondarySmall" size="small" onClick={onReload}>{msg("retry")}</AdminButton>}>{error.message}</Notice>}
			<DirectoryCard
				columns="minmax(240px, 1fr) minmax(100px, max-content) minmax(100px, max-content) minmax(90px, max-content) minmax(100px, max-content) max-content"
				head={["Method", "Type", "Providers", "Channels", "Updated", ""]}
				toolbar={<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameTypeOrId")} />}
				footer={
					!loading &&
					methods.length > 0 && <span>{query ? msg("templateOf_64f93023", { v0: visible.length, v1: plural(methods.length, "method") }) : plural(methods.length, "method")}</span>
				}>
				{loading ? (
					<SkeletonRows />
				) : !methods.length ? (
					<EmptyState icon={<Wallet size={20} />} title={msg("noPaymentMethodsYet")} text={msg("addAMethodBeforeCreatingChannelsForIt")} action={addButton} />
				) : !visible.length ? (
					<EmptyState icon={<Wallet size={20} />} title={msg("nothingMatchesTheSearch")} text={msg("tryAShorterQuery")} />
				) : (
					visible.map(method => {
						const served = usage.get(method.id) ?? [];
						const providers = uniqueSorted(served.map(channel => channel.provider?.name));
						const updated = formatDate(method.updated_at) ?? formatDate(method.created_at);
						return (
							<DirectoryRow key={method.id} label={msg("templateEdit_f1be7e63", { v0: method.name })} onOpen={() => GD.S_TERMINAL_EDITOR.invoke({ terminalID: method.id })}>
								<Cell kind="main">
									<EntityCell icon={<MethodTile type={method.type} />} name={method.name || msg("templateMethod_ff88fa4b", { v0: method.id })} sub={<Mono>#{method.id}</Mono>} />
								</Cell>
								<Cell label={msg("type")}>{methodTypeLabel(method.type)}</Cell>
								<Cell label={msg("providers")}>
									{providers.length ? (
										<Chips>
											{providers.slice(0, 3).map(name => (
												<Chip key={name}>{name}</Chip>
											))}
											{providers.length > 3 && <Muted>+{providers.length - 3}</Muted>}
										</Chips>
									) : (
										<Muted>{msg("noProviderYet")}</Muted>
									)}
								</Cell>
								<Cell label={msg("channels")}>
									{served.length ? (
										<LinkButton onClick={() => onShowChannels(method.id)} title={msg("showTheseChannels")}>
											{plural(served.length, "channel")} →
										</LinkButton>
									) : (
										<Muted>{msg("none")}</Muted>
									)}
								</Cell>
								<Cell label={msg("updated")}><DateTimeTooltip value={updated ? (formatDate(method.updated_at) ? method.updated_at : method.created_at) : undefined}>{updated ?? <Muted>—</Muted>}</DateTimeTooltip></Cell>
								<Cell kind="trail">
									<RowChevron />
								</Cell>
							</DirectoryRow>
						);
					})
				)}
			</DirectoryCard>
		</TabSurface>
	);
};
